import * as React from 'react';

import type {
    GetQuoteById_quote_subtotalLineItems_edges_node as QuoteSubtotalLineItemType,
    QuoteSubtotalItemsUpdateInput,
    GetQuoteById_quote_subtotalLineItems as SubtotalLineItems,
    GetQuoteById_quote_subtotal as Money,
    GetQuoteById_quote_total as TotalMoney,
} from 'nutshell-graphql-types';

import {SaveableWrapper} from '../../saveable-wrapper/saveable-wrapper';

import {QuoteSubtotalTable} from '../editable/quote-subtotal-table';
import {
    SUBTOTAL_TABLE,
    generateSubtotalLineItemsUpdateInput,
    generateSubtotalLineitemDefaults,
} from '../helpers';
import {QuoteSubtotal} from '../read-only/quote-subtotal';

export const MODIFIER_UNITS = {
    FIXED_AMOUNT: 'FIXED_AMOUNT',
    PERCENT: 'PERCENT',
} as const;

export const OPERATION_MAP = {
    ADD: 'ADD',
    SUBTRACT: 'SUBTRACT',
} as const;

type Props = {
    onSave: (arg1: QuoteSubtotalItemsUpdateInput) => void;
    onCancel: () => void;
    isActive: boolean;
    id: string;
    subtotalLineItems: SubtotalLineItems;
    subtotal: Money;
    total: TotalMoney;
    onToggleActive: (arg1: string) => void;
    forceSave: boolean;
    isImmutable: boolean;
};

export function QuoteSubtotalWrapper(props: Props) {
    const {id, onSave} = props;

    const [subtotalLineItems, setSubtotalLineItems] = React.useState<QuoteSubtotalLineItemType[]>(
        props.subtotalLineItems.edges.map((edge) => edge.node)
    );

    const idCounterRef = React.useRef<number>(0);

    React.useEffect(() => {
        setSubtotalLineItems(props.subtotalLineItems.edges.map((edge) => edge.node));
    }, [props.subtotalLineItems]);

    const handleSave = React.useCallback(() => {
        onSave(generateSubtotalLineItemsUpdateInput(subtotalLineItems, id));
    }, [id, onSave, subtotalLineItems]);

    React.useEffect(() => {
        if (props.forceSave) {
            handleSave();
        }
    }, [props.forceSave, handleSave]);

    const handleCancel = () => {
        setSubtotalLineItems(props.subtotalLineItems.edges.map((edge) => edge.node));
        props.onCancel();
    };

    const handleUpdateLineItem = React.useCallback((lineItem: QuoteSubtotalLineItemType) => {
        setSubtotalLineItems((prev) => {
            return prev.map((item) => (item.id === lineItem.id ? lineItem : item));
        });
    }, []);

    const handleDeleteRow = React.useCallback((deleteId: string) => {
        setSubtotalLineItems((prev) => {
            return prev.filter((item) => item.id !== deleteId);
        });
    }, []);

    const handleAddRow = React.useCallback(() => {
        const newLineItem = generateSubtotalLineitemDefaults(props.total, idCounterRef);
        // @ts-expect-error
        setSubtotalLineItems((prev) => [...prev, newLineItem]);
    }, [props.total]);

    const handleReorder = React.useCallback((startIndex: number, endIndex: number) => {
        setSubtotalLineItems((prevItems) => {
            const newItems = [...prevItems];
            const [movedItem] = newItems.splice(startIndex, 1);
            // @ts-expect-error
            newItems.splice(endIndex, 0, movedItem);

            return newItems;
        });
    }, []);

    return (
        <SaveableWrapper
            isActive={props.isActive}
            onSave={handleSave}
            onCancel={handleCancel}
            noPadding={true}
            onToggleActive={props.onToggleActive}
            sectionName={SUBTOTAL_TABLE}
            isImmutable={props.isImmutable}
        >
            {props.isActive ? (
                <QuoteSubtotalTable
                    subtotalLineItems={subtotalLineItems}
                    subtotal={props.subtotal}
                    total={props.total}
                    onUpdateLineItem={handleUpdateLineItem}
                    onDeleteRow={handleDeleteRow}
                    onAddRow={handleAddRow}
                    onReorder={handleReorder}
                />
            ) : (
                <QuoteSubtotal
                    subtotal={props.subtotal}
                    subtotalLineItems={props.subtotalLineItems}
                    total={props.total}
                    onAddRow={handleAddRow}
                    onToggleActive={props.onToggleActive}
                    sectionName={SUBTOTAL_TABLE}
                />
            )}
        </SaveableWrapper>
    );
}
